import React from "react";
import Tooltip from "../../../Project Page/Tooltip";
import CustomDocumentModal from "./CustomDocumentModal";
import { HelpIcon, BoltIcon, TrashIcon, CheckmarkIcon,AddIcon } from "./setupIcons";
import {
  TONE_OPTIONS,
  LENGTH_OPTIONS,
  COMPANION_DOCS,
  CUSTOM_INSTRUCTIONS_MAX,
} from "./setupData";

// Cap on total companion documents (defaults + custom).
const MAX_COMPANION_DOCS = 5;

const Badge = ({ children, variant = "required" }) => {
  const styles = {
    required: "bg-[#F7EBF5] text-[#A04A97]",
    recommended: "bg-[#FBF3E2] text-[#B7791F]",
    neutral: "bg-[#F3F4F6] text-[#6B7280]",
    default: "bg-[#F0F0F0] text-[#737373]",
    custom: "bg-[#0369A10F] text-[#0D4B6E]",
  }[variant];
  return (
    <span
      className={`px-2 py-[2px] rounded-md font-outfit text-[12px] font-medium ${styles}`}
    >
      {children}
    </span>
  );
};

const SectionTitle = ({ number, title, badge, badgeVariant, tooltip, action }) => (
  <div className="flex items-center gap-2 mb-1">
    <span className="text-[#A04A97] font-outfit text-base font-semibold">
      {number}
    </span>
    <h3 className="text-[#1A1A1A] font-outfit text-base font-medium">
      {title}
    </h3>
    {badge && <Badge variant={badgeVariant}>{badge}</Badge>}
    {tooltip ? (
      <Tooltip content={tooltip} placement="right">
        <HelpIcon />
      </Tooltip>
    ) : (
      <HelpIcon />
    )}
    {action && <div className="ml-auto">{action}</div>}
  </div>
);

const AddButton = ({ onClick, disabled, label }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={`flex items-center gap-2 font-outfit text-[14px] font-medium disabled:cursor-not-allowed ${
      disabled ? "text-[#A1A1A1]" : "text-[#A04A97]"
    }`}
  >
    <span
      className={`w-7 h-7 flex items-center justify-center rounded-full text-white text-[15px] leading-none ${
        disabled ? "bg-[#A1A1A1]" : "bg-[#A04A97]"
      }`}
    >
      <AddIcon />
    </span>
    {label}
  </button>
);

// Reusable add-list for USPs. Items are { id, value } (id null
// until persisted). `onRemoveItem`, when omitted, just drops from local state.
const AddList = ({
  items,
  setItems,
  placeholder,
  draft,
  setDraft,
  onCommit,
  quickAdd,
  max,
  onRemoveItem,
}) => {
  const atMax = max != null && items.length >= max;
  const hasValue = (value) =>
    items.some((it) => it.value.trim().toLowerCase() === value.trim().toLowerCase());
  const updateItem = (index, value) =>
    setItems(items.map((it, i) => (i === index ? { ...it, value } : it)));
  const removeItem = (index) => {
    if (onRemoveItem) onRemoveItem(items[index], index);
    else setItems(items.filter((_, i) => i !== index));
  };
  const addPreset = (value) => {
    if (atMax) return;
    if (!hasValue(value)) setItems([...items, { id: null, value }]);
  };

  return (
    <div className="border border-gray-200 rounded-[14px] p-4 !bg-[#FFFFFF] shadow-[0_1px_2px_0_rgba(26,10,30,0.04),_0_10px_20px_-22px_rgba(42,10,">
      {items.length > 0 && (
        <div className="mb-1">
          {items.map((item, index) => (
            <div
              key={item.id || `draft-${index}`}
              className="flex items-center gap-3 py-2 border-b border-gray-200 transition-colors focus-within:border-[#C95EBE]"
            >
              <input
                type="text"
                value={item.value}
                onChange={(e) => updateItem(index, e.target.value)}
                placeholder={placeholder}
                className="flex-1 border-0 font-outfit !text-[14px] leading-normal text-[#1A1A1A] placeholder:text-[#666666] placeholder:text-[14px] placeholder:font-normal focus:outline-none focus:ring-0 bg-transparent"
              />
              <button
                onClick={() => removeItem(index)}
                className="w-7 h-7 flex items-center justify-center rounded-md text-[#9CA3AF] hover:bg-gray-100 hover:text-[#E21919]"
                aria-label="Remove"
              >
                <TrashIcon />
              </button>
            </div>
          ))}
        </div>
      )}

      {!atMax && (
        <div className="flex items-center gap-3 py-2 mb-3 border-b border-gray-200 transition-colors focus-within:border-[#C95EBE]">
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                onCommit();
              }
            }}
            placeholder={placeholder}
            className="flex-1 border-0 font-outfit !text-[14px] leading-normal text-[#1A1A1A] placeholder:text-[#666666] placeholder:text-[14px] placeholder:font-normal focus:outline-none focus:ring-0 bg-transparent"
          />
        </div>
      )}

      {atMax && (
        <p className="font-outfit text-[13px] text-[#9CA3AF] mb-3">
          You've reached the maximum of {max}. Remove one to add another.
        </p>
      )}

      {quickAdd && quickAdd.length > 0 && (
        <div className="mt-5">
          <p className="font-outfit text-[12px] font-semibold tracking-[0.08em] text-[#9CA3AF] mb-3">
            QUICK ADD
          </p>
          <div className="flex flex-wrap gap-3">
            {quickAdd.map((preset) => {
              const alreadyAdded = hasValue(preset);
              const disabled = alreadyAdded || atMax;
              return (
                <button
                  key={preset}
                  onClick={() => addPreset(preset)}
                  disabled={disabled}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border font-outfit text-[13px] font-medium transition-colors ${
                    disabled
                      ? "border-gray-200 text-[#C9C9D0] cursor-not-allowed"
                      : "border-[#E7C9E3] text-[#A04A97] hover:bg-[#C95EBE0D]"
                  }`}
                >
                  <span className="text-base leading-none">+</span>
                  {preset}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const SquareCheckbox = ({ checked, onChange }) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={checked}
    onClick={onChange}
    className={`w-5 h-5 flex items-center justify-center rounded-[5px] border transition-colors flex-shrink-0 ${
      checked
        ? "bg-[#C95EBE] border-[#C95EBE]"
        : "bg-white border-gray-300 hover:border-[#C95EBE]"
    }`}
  >
    {checked && <CheckmarkIcon />}
  </button>
);

const SaveStatus = ({ status }) => {
  if (status === "idle") return null;
  const config = {
    saving: { text: "Saving…", className: "text-[#A04A97]", spin: true },
    saved: { text: "All changes saved", className: "text-[#16A34A]", spin: false },
    error: { text: "Couldn't save changes", className: "text-[#E21919]", spin: false },
  }[status];
  if (!config) return null;
  return (
    <span
      className={`flex items-center gap-1.5 font-outfit text-[13px] font-medium ${config.className}`}
    >
      {config.spin && (
        <span className="w-3.5 h-3.5 rounded-full border-2 border-current border-t-transparent animate-spin" />
      )}
      {config.text}
    </span>
  );
};

const ChevronDown = ({ className = "" }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className={className}>
    <path
      d="M6 9l6 6 6-6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const RadioDot = ({ selected }) =>
  selected ? (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="23"
      height="23"
      viewBox="0 0 23 23"
      fill="none"
      className="flex-shrink-0"
    >
      <path
        d="M11.5003 16.2915C12.826 16.2915 13.9561 15.8243 14.8904 14.8899C15.8248 13.9556 16.292 12.8255 16.292 11.4998C16.292 10.1741 15.8248 9.04411 14.8904 8.10973C13.9561 7.17536 12.826 6.70817 11.5003 6.70817C10.1746 6.70817 9.0446 7.17536 8.11022 8.10973C7.17585 9.04411 6.70866 10.1741 6.70866 11.4998C6.70866 12.8255 7.17585 13.9556 8.11022 14.8899C9.0446 15.8243 10.1746 16.2915 11.5003 16.2915ZM11.5003 21.0832C10.1746 21.0832 8.9288 20.8316 7.76283 20.3285C6.59685 19.8254 5.58262 19.1425 4.72012 18.28C3.85762 17.4175 3.1748 16.4033 2.67168 15.2373C2.16855 14.0714 1.91699 12.8255 1.91699 11.4998C1.91699 10.1741 2.16855 8.92831 2.67168 7.76234C3.1748 6.59636 3.85762 5.58213 4.72012 4.71963C5.58262 3.85713 6.59685 3.17432 7.76283 2.67119C8.9288 2.16807 10.1746 1.9165 11.5003 1.9165C12.826 1.9165 14.0719 2.16807 15.2378 2.67119C16.4038 3.17432 17.418 3.85713 18.2805 4.71963C19.143 5.58213 19.8258 6.59636 20.329 7.76234C20.8321 8.92831 21.0837 10.1741 21.0837 11.4998C21.0837 12.8255 20.8321 14.0714 20.329 15.2373C19.8258 16.4033 19.143 17.4175 18.2805 18.28C17.418 19.1425 16.4038 19.8254 15.2378 20.3285C14.0719 20.8316 12.826 21.0832 11.5003 21.0832ZM11.5003 19.1665C13.6406 19.1665 15.4535 18.4238 16.9389 16.9384C18.4243 15.453 19.167 13.6401 19.167 11.4998C19.167 9.35956 18.4243 7.54671 16.9389 6.0613C15.4535 4.57588 13.6406 3.83317 11.5003 3.83317C9.36005 3.83317 7.5472 4.57588 6.06178 6.0613C4.57637 7.54671 3.83366 9.35956 3.83366 11.4998C3.83366 13.6401 4.57637 15.453 6.06178 16.9384C7.5472 18.4238 9.36005 19.1665 11.5003 19.1665Z"
        fill="#A04A97"
      />
    </svg>
  ) : (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="23"
      height="23"
      viewBox="0 0 23 23"
      fill="none"
      className="flex-shrink-0"
    >
      <path
        d="M11.5003 21.0832C10.1746 21.0832 8.9288 20.8316 7.76283 20.3285C6.59685 19.8254 5.58262 19.1425 4.72012 18.28C3.85762 17.4175 3.1748 16.4033 2.67168 15.2373C2.16855 14.0714 1.91699 12.8255 1.91699 11.4998C1.91699 10.1741 2.16855 8.92831 2.67168 7.76234C3.1748 6.59636 3.85762 5.58213 4.72012 4.71963C5.58262 3.85713 6.59685 3.17432 7.76283 2.67119C8.9288 2.16807 10.1746 1.9165 11.5003 1.9165C12.826 1.9165 14.0719 2.16807 15.2378 2.67119C16.4038 3.17432 17.418 3.85713 18.2805 4.71963C19.143 5.58213 19.8258 6.59636 20.329 7.76234C20.8321 8.92831 21.0837 10.1741 21.0837 11.4998C21.0837 12.8255 20.8321 14.0714 20.329 15.2373C19.8258 16.4033 19.143 17.4175 18.2805 18.28C17.418 19.1425 16.4038 19.8254 15.2378 20.3285C14.0719 20.8316 12.826 21.0832 11.5003 21.0832ZM11.5003 19.1665C13.6406 19.1665 15.4535 18.4238 16.9389 16.9384C18.4243 15.453 19.167 13.6401 19.167 11.4998C19.167 9.35956 18.4243 7.54671 16.9389 6.0613C15.4535 4.57588 13.6406 3.83317 11.5003 3.83317C9.36005 3.83317 7.5472 4.57588 6.06178 6.0613C4.57637 7.54671 3.83366 9.35956 3.83366 11.4998C3.83366 13.6401 4.57637 15.453 6.06178 16.9384C7.5472 18.4238 9.36005 19.1665 11.5003 19.1665Z"
        fill="#D2CED8"
      />
    </svg>
  );

// 04 Proposal Structure — single-select between an AI-extracted outline and a
// saved section template. Selecting "Auto-Extract" commits immediately; the
// template branch commits only once a template is picked (a template source
// without a template_id is rejected by the backend). Presentational: all state
// and persistence live in ProposalPage.
const ProposalStructureSection = ({
  structureSource,
  onSelectAi,
  onChooseTemplateSource,
  selectedTemplateId,
  selectedTemplateName,
  templateUnavailable,
  templates = [],
  templatesLoading,
  templatesError,
  onReloadTemplates,
  onPickTemplate,
  onViewAllTemplates,
}) => {
  const [dropdownOpen, setDropdownOpen] = React.useState(false);
  const [pendingId, setPendingId] = React.useState(selectedTemplateId || null);
  const wrapRef = React.useRef(null);
  const isTemplate = structureSource === "template";

  React.useEffect(() => {
    if (!dropdownOpen) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [dropdownOpen]);

  // Every time the picker opens — by any path (radio, link, or toggle) — reseed
  // the highlight from the committed template. So a choice that was highlighted
  // but never confirmed (dropdown closed via Cancel / click-outside) is discarded
  // and does not reappear on the next open.
  React.useEffect(() => {
    if (dropdownOpen) setPendingId(selectedTemplateId || null);
  }, [dropdownOpen, selectedTemplateId]);

  const openPicker = () => {
    setDropdownOpen(true);
    if (!isTemplate) onChooseTemplateSource();
  };

  const confirmSelection = () => {
    const tpl = templates.find((t) => String(t.id) === String(pendingId));
    if (!tpl) return;
    onPickTemplate(tpl);
    setDropdownOpen(false);
  };

  const linkLabel = isTemplate && selectedTemplateName ? selectedTemplateName : "Select Template";

  return (
    <div className="relative mb-6 pb-6 after:content-[''] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-px after:bg-gradient-to-r after:from-[#E6E2EA] after:via-[#EFEDF1] after:to-[#EFEDF1]/0">
      <SectionTitle
        number="05"
        title="Proposal Structure"
        badge="Required"
        badgeVariant="required"
        tooltip="Select how the proposal section outline is created. Choose AI or a reusable template to define the initial structure. Individual sections can still be edited after creation."
      />
      <p className="text-[#767676] font-outfit text-[14px] mb-4">
        Define the initial section structure for your proposal using AI or a reusable template.
      </p>

      <div
        ref={wrapRef}
        className="relative border border-gray-200 rounded-[14px] !bg-[#FFFFFF] shadow-[0_1px_2px_0_rgba(26,10,30,0.04),_0_10px_20px_-22px_rgba(42,10,34,0.22)]"
      >
        {/* Use a Template */}
        <div className="flex items-start gap-3 px-4 py-3.5">
          <button
            type="button"
            onClick={openPicker}
            className="pt-[2px]"
            aria-label="Use a template"
          >
            <RadioDot selected={isTemplate} />
          </button>
          <div className="flex-1 cursor-pointer" onClick={openPicker}>
            <div className="font-outfit text-[14px] font-medium text-[#1A1A1A]">
              Use a Template
            </div>
            <p className="font-outfit text-[13px] text-[#767676] mt-0.5">
              Pick a saved section outline. Best when your team has a standard structure
            </p>
          </div>
          <button
            type="button"
            onClick={() => (isTemplate ? setDropdownOpen((o) => !o) : openPicker())}
            className={`flex items-center gap-1.5 font-outfit text-[14px] font-medium whitespace-nowrap max-w-[45%] transition-colors ${
              isTemplate ? "text-[#A04A97]" : "text-[#9CA3AF] hover:text-[#A04A97]"
            }`}
          >
            <span className="truncate">{linkLabel}</span>
            <ChevronDown
              className={`flex-shrink-0 transition-transform ${dropdownOpen ? "rotate-180" : ""}`}
            />
          </button>
        </div>

        {templateUnavailable && (
          <p className="px-5 -mt-2 pb-3 font-outfit text-[12px] text-[#B7791F]">
            The selected template is no longer available. Please choose another.
          </p>
        )}

        {/* Auto-Extract from RFP */}
        <div className="flex items-start gap-3 px-4 py-3.5 border-t border-gray-100">
          <button
            type="button"
            onClick={onSelectAi}
            className="pt-[2px]"
            aria-label="Auto-extract from RFP"
          >
            <RadioDot selected={!isTemplate} />
          </button>
          <div className="flex-1 cursor-pointer" onClick={onSelectAi}>
            <div className="font-outfit text-[15px] font-medium text-[#1A1A1A]">
              Auto-Extract from RFP
            </div>
            <p className="font-outfit text-[13px] text-[#767676] mt-0.5">
              Let docusensa generate the sections from RFP requirements. Best for new or one-off bids.
            </p>
          </div>
        </div>

        {/* Choose a Template popover */}
        {dropdownOpen && (
          <div className="absolute right-3 top-[70px] z-50 w-[380px] max-w-[calc(100%-24px)] bg-white rounded-[14px] border border-[#F0E9EF] shadow-[0_18px_50px_-12px_rgba(42,10,34,0.28)] p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="font-outfit text-[15px] font-semibold text-[#1A1A1A]">
                Choose a Template
              </span>
              <button
                type="button"
                onClick={onViewAllTemplates}
                className="font-outfit text-[13px] font-medium text-[#A04A97] underline underline-offset-2"
              >
                View All
              </button>
            </div>

            <div className="max-h-[220px] overflow-y-auto custom-scrollbar space-y-2 pr-1">
              {templatesLoading ? (
                <div className="py-8 flex items-center justify-center gap-2 text-[#9CA3AF] font-outfit text-[13px]">
                  <span className="w-4 h-4 rounded-full border-2 border-current border-t-transparent animate-spin" />
                  Loading templates…
                </div>
              ) : templatesError ? (
                <div className="py-6 text-center">
                  <p className="font-outfit text-[13px] text-[#E21919] mb-2">
                    Couldn't load templates.
                  </p>
                  <button
                    type="button"
                    onClick={onReloadTemplates}
                    className="font-outfit text-[13px] font-medium text-[#A04A97]"
                  >
                    Try again
                  </button>
                </div>
              ) : templates.length === 0 ? (
                <div className="py-6 text-center">
                  <p className="font-outfit text-[13px] text-[#767676] mb-1">
                    No templates yet.
                  </p>
                  <button
                    type="button"
                    onClick={onViewAllTemplates}
                    className="font-outfit text-[13px] font-medium text-[#A04A97]"
                  >
                    Create one
                  </button>
                </div>
              ) : (
                templates.map((tpl) => {
                  const isSel = String(pendingId) === String(tpl.id);
                  const isOrg = tpl.visibility === "organization";
                  return (
                    <button
                      key={tpl.id}
                      type="button"
                      onClick={() => setPendingId(tpl.id)}
                      className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-[10px] border text-left transition-colors ${
                        isSel
                          ? "border-[#C95EBE] bg-[#C95EBE0D]"
                          : "border-gray-200 hover:border-[#E7C9E3]"
                      }`}
                    >
                      <span className="flex-1 font-outfit text-[14px] font-medium text-[#1A1A1A] truncate">
                        {tpl.name}
                      </span>
                      <Badge variant="neutral">{isOrg ? "Org" : "Personal"}</Badge>
                      <ChevronDown className="-rotate-90 text-[#9CA3AF] flex-shrink-0" />
                    </button>
                  );
                })
              )}
            </div>

            <div className="flex items-center justify-center gap-3 mt-4">
              <button
                type="button"
                onClick={() => setDropdownOpen(false)}
                className="px-6 py-2 rounded-md border border-[#A04A97] text-[#A04A97] font-outfit text-[14px] font-medium hover:bg-[#A04A970D] transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmSelection}
                disabled={!pendingId}
                className="px-6 py-2 rounded-md bg-[#A04A97] text-white font-outfit text-[14px] font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#8f4288] transition-colors"
              >
                Select Template
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ProposalSetupStep = ({
  selectedTone,
  setSelectedTone,
  selectedLength,
  setSelectedLength,
  usps,
  setUsps,
  customInstructions,
  setCustomInstructions,
  selectedCompanionDocs,
  toggleCompanionDoc,
  saveStatus = "idle",
  onRemoveUsp,
  customDocs = [],
  onCreateCustomDoc,
  onToggleCustomDoc,
  structureSource = "agentic",
  onSelectAiStructure,
  onChooseTemplateSource,
  selectedTemplateId,
  selectedTemplateName,
  templateUnavailable,
  templates = [],
  templatesLoading,
  templatesError,
  onReloadTemplates,
  onPickTemplate,
  onViewAllTemplates,
}) => {
  const [isCustomDocOpen, setIsCustomDocOpen] = React.useState(false);
  const [uspDraft, setUspDraft] = React.useState("");
  const activeTone = TONE_OPTIONS.find((t) => t.label === selectedTone);
  const activeLength = LENGTH_OPTIONS.find((o) => o.value === selectedLength);

  const commitDraft = (draft, setDraft, items, setItems, max) => {
    const value = draft.trim();
    if (!value || (max != null && items.length >= max)) return;
    const exists = items.some(
      (it) => it.value.trim().toLowerCase() === value.toLowerCase()
    );
    if (!exists) setItems([...items, { id: null, value }]);
    setDraft("");
  };

  return (
    <div className="h-full flex flex-col bg-[#FAF9FB]">
      <div className="flex-shrink-0 pr-2 bg-[#FAF9FB]">
        <div className="flex items-center justify-between gap-3 mb-1">
          <h1 className="text-[#1A1A1A] font-outfit text-[20px] font-semibold">
            Set up your proposal
          </h1>
          <SaveStatus status={saveStatus} />
        </div>
        <p className="text-[#767676] font-outfit text-[14px] mb-3">
          Define how the AI should write before drafting. Open the Copilot
          anytime for the RFP brief and suggestions.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar pr-2 [scrollbar-gutter:stable] bg-[#FAF9FB]">
        {/* 01 Proposal Tone */}
        <div className="relative mb-6 pb-6 after:content-[''] bg-[#FAF9FB]">
          <SectionTitle
            number="01"
            title="Proposal Tone"
            badge="Required"
            badgeVariant="required"
            tooltip="Select the overall writing style for generated responses. Different tones help tailor the proposal for technical evaluators, executive stakeholders, or formal enterprise submissions."
          />
          <p className="text-[#767676] font-outfit text-[14px] mb-4">
            The impression you want this proposal to leave with the evaluator.
          </p>

          <div className="border border-gray-200 rounded-[14px] p-5 !bg-[#FFFFFF] shadow-[0_1px_2px_0_rgba(26,10,30,0.04),_0_10px_20px_-22px_rgba(42,10,34,0.22)]">
            <div className="flex flex-wrap gap-3">
              {TONE_OPTIONS.map((tone) => {
                const isSelected = tone.label === selectedTone;
                const isDisabled = tone.disabled;
                return (
                  <button
                    key={tone.label}
                    onClick={() => !isDisabled && setSelectedTone(tone.label)}
                    disabled={isDisabled}
                    title={isDisabled ? "Coming soon" : undefined}
                    className={`px-3 py-1 rounded-full font-outfit text-[14px] font-medium border transition-[border-color] duration-150 ${
                      isDisabled
                        ? "bg-[#F3F4F6] text-[#C9C9D0] border-[#ECE9EE] cursor-not-allowed"
                        : isSelected
                        ? "bg-[#C95EBE] text-white border-[#C95EBE]"
                        : "bg-white text-[#000000B2] border-[#ECE9EE] hover:border-[#C95EBE]"
                    }`}
                  >
                    {tone.label}
                  </button>
                );
              })}
            </div>

            {activeTone && (
              <div className="flex items-center gap-2 mt-5">
                <BoltIcon />
                <span className="text-[#A04A97] font-outfit text-[14px] font-medium">
                  {activeTone.description}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* 02 Section Length */}
        <div className="relative mb-6 pb-6 after:content-[''] bg-[#FAF9FB]">
          <SectionTitle
            number="02"
            title="Section Length"
            tooltip="How much prose each section and subsection runs to. Every requirement is answered at all three settings — shorter means more compressed writing, never less coverage."
          />
          <p className="text-[#767676] font-outfit text-[14px] mb-4">
            How much room each section gets. Requirement coverage does not change.
          </p>

          <div className="border border-gray-200 rounded-[14px] p-5 !bg-[#FFFFFF] shadow-[0_1px_2px_0_rgba(26,10,30,0.04),_0_10px_20px_-22px_rgba(42,10,34,0.22)]">
            <div className="flex flex-wrap gap-3">
              {LENGTH_OPTIONS.map((opt) => {
                const isSelected = opt.value === selectedLength;
                return (
                  <button
                    key={opt.value}
                    onClick={() => setSelectedLength(opt.value)}
                    aria-pressed={isSelected}
                    className={`px-3 py-1 rounded-full font-outfit text-[14px] font-medium border transition-[border-color] duration-150 ${
                      isSelected
                        ? "bg-[#C95EBE] text-white border-[#C95EBE]"
                        : "bg-white text-[#000000B2] border-[#ECE9EE] hover:border-[#C95EBE]"
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>

            {activeLength && (
              <div className="flex items-center gap-2 mt-5">
                <BoltIcon />
                <span className="text-[#A04A97] font-outfit text-[14px] font-medium">
                  {activeLength.description}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* 03 Unique Selling Propositions */}
        <div className="relative mb-6 pb-6 after:content-[''] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-px after:bg-gradient-to-r after:from-[#E6E2EA] after:via-[#EFEDF1] after:to-[#EFEDF1]/0">
          <SectionTitle
            number="03"
            title="Unique Selling Propositions"
            badge="Recommended"
            badgeVariant="recommended"
            tooltip="Add concise differentiators that make your solution more competitive. Include measurable outcomes, domain expertise, certifications, delivery advantages, or proven results relevant to this opportunity."
            action={
              <AddButton
                label="Add a strength"
                disabled={!uspDraft.trim() || usps.length >= 5}
                onClick={() =>
                  commitDraft(uspDraft, setUspDraft, usps, setUsps, 5)
                }
              />
            }
          />
          <p className="text-[#767676] font-outfit text-[14px] mb-4">
            The strengths the AI should weave through every section. Edit, remove
            or add your own.
          </p>
          <AddList
            items={usps}
            setItems={setUsps}
            placeholder="e.g. Proven 28% avg TCO reduction"
            draft={uspDraft}
            setDraft={setUspDraft}
            onCommit={() => commitDraft(uspDraft, setUspDraft, usps, setUsps, 5)}
            max={5}
            onRemoveItem={onRemoveUsp}
          />
        </div>

        {/* 03 Custom Instructions */}
        <div className="relative mb-6 pb-6 after:content-[''] after:absolute after:bottom-0 after:left-0 after:right-0 after:h-px after:bg-gradient-to-r after:from-[#E6E2EA] after:via-[#EFEDF1] after:to-[#EFEDF1]/0">
          <SectionTitle
            number="04"
            title="Custom Instructions"
            tooltip="Any guidance that applies across the whole proposal — citation rules, formatting preferences, or constraints the AI should always follow when writing."
          />
          <p className="text-[#767676] font-outfit text-[14px] mb-4">
            Guide AI with specific writing preferences or requirements.
          </p>
          <div className="border border-gray-200 rounded-[14px] p-4 !bg-[#FFFFFF] shadow-[0_1px_2px_0_rgba(26,10,30,0.04),_0_10px_20px_-22px_rgba(42,10,34,0.22)]">
            <textarea
              value={customInstructions}
              onChange={(e) =>
                setCustomInstructions(
                  e.target.value.slice(0, CUSTOM_INSTRUCTIONS_MAX)
                )
              }
              rows={3}
              placeholder="e.g. Always cite ISO 27001 in security sections. Do not make pricing commitments in technical sections."
              className="w-full resize-none font-outfit text-[14px] text-[#1A1A1A] placeholder:text-[#666666] placeholder:text-[14px] placeholder:font-normal focus:outline-none bg-transparent"
            />
            <div className="flex justify-end">
              <span className="font-outfit text-[12px] text-[#9CA3AF]">
                {customInstructions.length}/{CUSTOM_INSTRUCTIONS_MAX}
              </span>
            </div>
          </div>
        </div>

        {/* 04 Proposal Structure */}
        <ProposalStructureSection
          structureSource={structureSource}
          onSelectAi={onSelectAiStructure}
          onChooseTemplateSource={onChooseTemplateSource}
          selectedTemplateId={selectedTemplateId}
          selectedTemplateName={selectedTemplateName}
          templateUnavailable={templateUnavailable}
          templates={templates}
          templatesLoading={templatesLoading}
          templatesError={templatesError}
          onReloadTemplates={onReloadTemplates}
          onPickTemplate={onPickTemplate}
          onViewAllTemplates={onViewAllTemplates}
        />

        {/* 05 Companion Documents */}
        <div className="mb-6">
          <SectionTitle
            number="06"
            title="Companion Documents"
            tooltip="Supporting files that go with your proposal — cover letters, certificates, appendices. Add auto-created ones, pick from your Knowledge Base, or create new with AI."
            action={
              <AddButton
                label="Create Custom"
                disabled={
                  COMPANION_DOCS.length + customDocs.length >= MAX_COMPANION_DOCS
                }
                onClick={() => setIsCustomDocOpen(true)}
              />
            }
          />
          <p className="text-[#767676] font-outfit text-[14px] mb-4">
            Select supporting documents to generate alongside the proposal.
          </p>
          <div className="border border-gray-200 rounded-[14px] p-4 !bg-[#FFFFFF] shadow-[0_1px_2px_0_rgba(26,10,30,0.04),_0_10px_20px_-22px_rgba(42,10,34,0.22)]">
            <div className="space-y-4 mb-4">
              {COMPANION_DOCS.map((doc) => {
                const checked = selectedCompanionDocs.includes(doc.id);
                return (
                  <div key={doc.id} className="flex items-start gap-3">
                    <div className="pt-[2px]">
                      <SquareCheckbox
                        checked={checked}
                        onChange={() => toggleCompanionDoc(doc.id)}
                      />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-outfit text-[14px] font-medium text-[#1A1A1A]">
                          {doc.title}
                        </span>
                        {doc.badge && <Badge variant="default">{doc.badge}</Badge>}
                      </div>
                      <p className="font-outfit text-[13px] text-[#767676]">
                        {doc.description}
                      </p>
                    </div>
                  </div>
                );
              })}

              {customDocs.map((doc) => (
                <div key={doc.id} className="flex items-start gap-3">
                  <div className="pt-[2px]">
                    <SquareCheckbox
                      checked={doc.enabled}
                      onChange={() => onToggleCustomDoc && onToggleCustomDoc(doc.id)}
                    />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-outfit text-[14px] font-medium text-[#1A1A1A]">
                        {doc.name}
                      </span>
                      <Badge variant="custom">Custom</Badge>
                    </div>
                    {doc.description && (
                      <p className="font-outfit text-[13px] text-[#767676]">
                        {doc.description}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {COMPANION_DOCS.length + customDocs.length >= MAX_COMPANION_DOCS && (
              <p className="font-outfit text-[13px] text-[#9CA3AF]">
                You've reached the maximum of {MAX_COMPANION_DOCS} documents.
                Remove one to add another.
              </p>
            )}
          </div>
        </div>
      </div>

      <CustomDocumentModal
        isOpen={isCustomDocOpen}
        onClose={() => setIsCustomDocOpen(false)}
        onSave={onCreateCustomDoc}
      />
    </div>
  );
};

export default ProposalSetupStep;
